import { useCallback, useEffect, useState } from 'react';

/** The non-standard event Chromium fires when the app can be installed. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'swarm.installHintDismissed';

export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  const media =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(display-mode: standalone)')
      : null;
  return media?.matches === true || nav.standalone === true;
}

/** iPhone/iPad (iPadOS reports itself as a Mac with touch). */
export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && navigator.maxTouchPoints > 1);
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export type InstallState =
  | { kind: 'installed' }
  /** Chromium: a native prompt is available. */
  | { kind: 'prompt'; install: () => Promise<void> }
  /** iOS Safari: only the manual way "Share → Add to Home Screen". */
  | { kind: 'ios' }
  | { kind: 'unavailable' };

/** Install state of the app on this device (Android/Chrome prompt, iOS instructions). */
export function useInstall(): InstallState & { dismissed: boolean; dismiss: () => void } {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [dismissed, setDismissed] = useState(readDismissed);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setEvent(null);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Private mode: the hint just returns next time.
    }
    setDismissed(true);
  }, []);

  const install = useCallback(async () => {
    if (!event) return;
    await event.prompt();
    await event.userChoice;
    setEvent(null);
  }, [event]);

  let state: InstallState;
  if (installed) state = { kind: 'installed' };
  else if (event) state = { kind: 'prompt', install };
  else if (isIos()) state = { kind: 'ios' };
  else state = { kind: 'unavailable' };
  return { ...state, dismissed, dismiss };
}
